/**
 * Destructive Filter menu definitions. Shader `uType` values must stay in sync
 * with the constants in `src/gl/shaders/filter.frag.glsl`.
 */

export type FilterId =
  | 'gaussianBlur'
  | 'boxBlur'
  | 'motionBlur'
  | 'radialBlur'
  | 'unsharpMask'
  | 'smartSharpen'
  | 'addNoise'
  | 'median'
  | 'dustAndScratches'
  | 'highPass'
  | 'emboss'
  | 'findEdges'
  | 'clouds'
  | 'custom';

export const FILTER_TYPES: Record<FilterId, number> = {
  gaussianBlur: 0,
  boxBlur: 1,
  motionBlur: 2,
  radialBlur: 3,
  unsharpMask: 4,
  smartSharpen: 5,
  addNoise: 6,
  median: 7,
  dustAndScratches: 8,
  highPass: 9,
  emboss: 10,
  findEdges: 11,
  clouds: 12,
  custom: 13,
};

export type FilterControl =
  | {
      kind: 'slider';
      key: string;
      label: string;
      min: number;
      max: number;
      step: number;
      defaultValue: number;
      suffix?: string;
    }
  | { kind: 'toggle'; key: string; label: string; defaultValue: number }
  | {
      kind: 'switch';
      key: string;
      label: string;
      options: { value: number; label: string }[];
      defaultValue: number;
    };

export interface FilterDef {
  id: FilterId;
  label: string;
  group: string;
  type: number;
  controls: FilterControl[];
  /** Filters with no meaningful identity state always change the image. */
  alwaysApplies?: boolean;
  pack: (values: Record<string, number>) => Float32Array;
}

/** Length of the uniform array every filter packs into. */
export const FILTER_PARAM_FLOATS = 16;

/** 3×3 identity: only the centre cell contributes. */
export const CUSTOM_IDENTITY_KERNEL = [0, 0, 0, 0, 1, 0, 0, 0, 0];

function slider(
  key: string,
  label: string,
  min: number,
  max: number,
  defaultValue: number,
  extra: Partial<Extract<FilterControl, { kind: 'slider' }>> = {},
): FilterControl {
  return { kind: 'slider', key, label, min, max, step: 1, defaultValue, ...extra };
}

function toggle(key: string, label: string, defaultValue = 0): FilterControl {
  return { kind: 'toggle', key, label, defaultValue };
}

function sw(
  key: string,
  label: string,
  options: { value: number; label: string }[],
  defaultValue: number,
): FilterControl {
  return { kind: 'switch', key, label, options, defaultValue };
}

function packSlots(slots: number[]): Float32Array {
  const data = new Float32Array(FILTER_PARAM_FLOATS);
  data.set(slots.slice(0, FILTER_PARAM_FLOATS));
  return data;
}

function customControls(): FilterControl[] {
  const controls: FilterControl[] = [
    slider('scale', 'Scale', -100, 100, 1, { step: 0.1 }),
    slider('offset', 'Offset', -255, 255, 0),
  ];
  for (let i = 0; i < 9; i += 1) {
    controls.push(
      slider(`k${i}`, `k${i}`, -999, 999, CUSTOM_IDENTITY_KERNEL[i], { step: 0.01 }),
    );
  }
  return controls;
}

export const FILTERS: Record<FilterId, FilterDef> = {
  gaussianBlur: {
    id: 'gaussianBlur',
    label: 'Gaussian Blur',
    group: 'Blur',
    type: FILTER_TYPES.gaussianBlur,
    controls: [slider('radius', 'Radius', 0.1, 250, 5, { step: 0.1, suffix: 'px' })],
    pack: (v) => packSlots([v.radius]),
  },
  boxBlur: {
    id: 'boxBlur',
    label: 'Box Blur',
    group: 'Blur',
    type: FILTER_TYPES.boxBlur,
    controls: [slider('radius', 'Radius', 1, 100, 5, { suffix: 'px' })],
    pack: (v) => packSlots([v.radius]),
  },
  motionBlur: {
    id: 'motionBlur',
    label: 'Motion Blur',
    group: 'Blur',
    type: FILTER_TYPES.motionBlur,
    controls: [
      slider('distance', 'Distance', 1, 999, 10, { suffix: 'px' }),
      slider('angle', 'Angle', -360, 360, 0, { suffix: '°' }),
    ],
    pack: (v) => packSlots([v.distance, v.angle]),
  },
  radialBlur: {
    id: 'radialBlur',
    label: 'Radial Blur',
    group: 'Blur',
    type: FILTER_TYPES.radialBlur,
    controls: [
      slider('amount', 'Amount', 1, 100, 10),
      sw('method', 'Method', [
        { value: 0, label: 'Spin' },
        { value: 1, label: 'Zoom' },
      ], 0),
      sw('quality', 'Quality', [
        { value: 0, label: 'Draft' },
        { value: 1, label: 'Good' },
        { value: 2, label: 'Best' },
      ], 1),
    ],
    pack: (v) => packSlots([v.amount, v.method, v.quality]),
  },
  unsharpMask: {
    id: 'unsharpMask',
    label: 'Unsharp Mask',
    group: 'Sharpen',
    type: FILTER_TYPES.unsharpMask,
    controls: [
      slider('amount', 'Amount', 1, 500, 50, { suffix: '%' }),
      slider('radius', 'Radius', 0.1, 250, 1, { step: 0.1, suffix: 'px' }),
      slider('threshold', 'Threshold', 0, 255, 0),
    ],
    pack: (v) => packSlots([v.amount / 100, v.radius, v.threshold / 255]),
  },
  smartSharpen: {
    id: 'smartSharpen',
    label: 'Smart Sharpen',
    group: 'Sharpen',
    type: FILTER_TYPES.smartSharpen,
    controls: [
      slider('amount', 'Amount', 1, 500, 100, { suffix: '%' }),
      slider('radius', 'Radius', 0.1, 64, 1, { step: 0.1, suffix: 'px' }),
      slider('reduceNoise', 'Reduce Noise', 0, 100, 0, { suffix: '%' }),
      sw('remove', 'Remove', [
        { value: 0, label: 'Gaussian Blur' },
        { value: 1, label: 'Lens Blur' },
        { value: 2, label: 'Motion Blur' },
      ], 0),
    ],
    pack: (v) => packSlots([v.amount / 100, v.radius, v.reduceNoise / 100, v.remove]),
  },
  addNoise: {
    id: 'addNoise',
    label: 'Add Noise',
    group: 'Noise',
    type: FILTER_TYPES.addNoise,
    controls: [
      slider('amount', 'Amount', 0.1, 400, 25, { step: 0.1, suffix: '%' }),
      sw('distribution', 'Distribution', [
        { value: 0, label: 'Uniform' },
        { value: 1, label: 'Gaussian' },
      ], 0),
      toggle('monochromatic', 'Monochromatic', 1),
    ],
    pack: (v) => packSlots([v.amount / 100, v.distribution, v.monochromatic]),
  },
  median: {
    id: 'median',
    label: 'Median',
    group: 'Noise',
    type: FILTER_TYPES.median,
    controls: [slider('radius', 'Radius', 1, 100, 1, { suffix: 'px' })],
    pack: (v) => packSlots([v.radius]),
  },
  dustAndScratches: {
    id: 'dustAndScratches',
    label: 'Dust & Scratches',
    group: 'Noise',
    type: FILTER_TYPES.dustAndScratches,
    controls: [
      slider('radius', 'Radius', 1, 100, 1, { suffix: 'px' }),
      slider('threshold', 'Threshold', 0, 255, 0),
    ],
    pack: (v) => packSlots([v.radius, v.threshold / 255]),
  },
  highPass: {
    id: 'highPass',
    label: 'High Pass',
    group: 'Other',
    type: FILTER_TYPES.highPass,
    controls: [slider('radius', 'Radius', 0.1, 250, 10, { step: 0.1, suffix: 'px' })],
    pack: (v) => packSlots([v.radius]),
  },
  emboss: {
    id: 'emboss',
    label: 'Emboss',
    group: 'Stylize',
    type: FILTER_TYPES.emboss,
    controls: [
      slider('angle', 'Angle', -180, 180, 135, { suffix: '°' }),
      slider('height', 'Height', 1, 100, 3, { suffix: 'px' }),
      slider('amount', 'Amount', 1, 500, 100, { suffix: '%' }),
    ],
    pack: (v) => packSlots([v.angle, v.height, v.amount / 100]),
  },
  findEdges: {
    id: 'findEdges',
    label: 'Find Edges',
    group: 'Stylize',
    type: FILTER_TYPES.findEdges,
    controls: [],
    alwaysApplies: true,
    pack: () => packSlots([]),
  },
  clouds: {
    id: 'clouds',
    label: 'Clouds',
    group: 'Render',
    type: FILTER_TYPES.clouds,
    controls: [],
    alwaysApplies: true,
    pack: () => packSlots([]),
  },
  custom: {
    id: 'custom',
    label: 'Custom',
    group: 'Other',
    type: FILTER_TYPES.custom,
    controls: customControls(),
    pack: (v) =>
      packSlots([
        v.scale,
        v.offset / 255,
        0,
        0,
        v.k0,
        v.k1,
        v.k2,
        0,
        v.k3,
        v.k4,
        v.k5,
        0,
        v.k6,
        v.k7,
        v.k8,
        0,
      ]),
  },
};

export const FILTER_ORDER: FilterId[] = [
  'gaussianBlur',
  'boxBlur',
  'motionBlur',
  'radialBlur',
  'unsharpMask',
  'smartSharpen',
  'addNoise',
  'median',
  'dustAndScratches',
  'highPass',
  'emboss',
  'findEdges',
  'clouds',
  'custom',
];

export const FILTER_GROUPS: { group: string; ids: FilterId[] }[] = [
  { group: 'Blur', ids: ['gaussianBlur', 'boxBlur', 'motionBlur', 'radialBlur'] },
  { group: 'Sharpen', ids: ['unsharpMask', 'smartSharpen'] },
  { group: 'Noise', ids: ['addNoise', 'median', 'dustAndScratches'] },
  { group: 'Stylize', ids: ['emboss', 'findEdges'] },
  { group: 'Render', ids: ['clouds'] },
  { group: 'Other', ids: ['highPass', 'custom'] },
];

export function defaultFilterValues(id: FilterId): Record<string, number> {
  const values: Record<string, number> = {};
  for (const control of FILTERS[id].controls) values[control.key] = control.defaultValue;
  return values;
}

export function isFilterNeutral(id: FilterId, values: Record<string, number>): boolean {
  const definition = FILTERS[id];
  if (definition.alwaysApplies) return false;
  return definition.controls.every((control) => values[control.key] === control.defaultValue);
}

export function packFilter(id: FilterId, values: Record<string, number>): Float32Array {
  return FILTERS[id].pack(values);
}
